/**
 * @file    useChartFilters.ts
 * @scope   SHARED
 * @summary Owns the filtering domain of the drawer: the available date ranges, the selected one,
 *          and the hierarchy / advanced filter selections.
 * @consumes Guardrails chart config payload, `customWidget.customWidgetConfig.Filters`
 * @produces Filter option lists, current selections, change handlers and `buildCurrentSelection`
 * @sideEffects Mutates `customWidget.customWidgetConfig.Filters` in place when a default or a new
 *              date range is applied (the original drawer relied on this, see WHY notes below).
 * @notes Shared: legacy charts use all three filter kinds; Enterprise V2 uses hierarchy/advanced
 *        filters but never the date filter, which is why `buildCurrentSelection` can drop it.
 */
import { useState } from 'react';
import { DateFilter, FilterDateOption, Widget } from 'models/WidgetModel';
import { convertJsonToAdvancedFilter, convertJsonToHierarchyFilter } from 'helpers/utilities';

/** Everything the drawer needs from the filtering domain. */
export type UseChartFiltersResult = {
  /** Date ranges offered by Guardrails for the current chart. */
  chartFilters: any[];
  /** Date range currently applied. */
  selectedFilteringDateRange: any;
  /** Hierarchy filter selection, already converted to the persisted shape. */
  hierarchyFiltersValues: any[];
  /** Advanced filter selection, already converted to the persisted shape. */
  advancedFiltersValues: any[];
  /** Parses a Guardrails chart config: stores the date ranges and applies the default one. */
  parseChartFilters: (chartData: any) => void;
  /** Applies a date range picked in the calendar modal. */
  applyDateRange: (newValue: any) => void;
  /** Handler for the hierarchy filter component. */
  handleHierarchyFilterChange: (newValue: any) => void;
  /** Handler for the advanced filter component. */
  handleAdvancedFilterChange: (newValue: any) => void;
  /** Builds the `Filters.CurrentSelection` object persisted in the widget config. */
  buildCurrentSelection: (isDiyWidgetEngine: boolean) => { currentSelection: any };
};

/** Options accepted by {@link useChartFilters}. */
export type UseChartFiltersParams = {
  /** Widget currently being edited. */
  customWidget: any;
  /** ENTERPRISE-ONLY: `true` while editing an Enterprise V2 widget (skips the default date range). */
  isDiyWidgetEngine: boolean;
};

export const useChartFilters = ({
  customWidget,
  isDiyWidgetEngine,
}: UseChartFiltersParams): UseChartFiltersResult => {
  const [chartFilters, setChartFilters] = useState<any[]>([]);
  const [selectedFilteringDateRange, setSelectedFilteringDateRange] = useState<any>({});
  const [hierarchyFiltersValues, setHierarchyFiltersValues] = useState<any>([]);
  const [advancedFiltersValues, setAdvancedFiltersValues] = useState<any>([]);

  // WHY: these flags record that the user *touched* a filter group. Without them an empty
  // selection would be indistinguishable from "never edited", and `buildCurrentSelection` would
  // resurrect the persisted filters the user just cleared.
  const [hasHierarchyFilters, setHasHierarchyFilters] = useState<boolean>(false);
  const [hasAdvancedFilters, setHasAdvancedFilters] = useState<boolean>(false);

  /**
   * Applies the default date range ("Month To Date") the first time a chart is configured.
   *
   * Skipped for query-backed and Enterprise V2 widgets: neither of them is date-filtered, and
   * injecting MTD would silently narrow their results.
   */
  const applyDefaultDateFilter = (chartData: any) => {
    let defaultFilter: DateFilter;
    let dateFilter: DateFilter | undefined | null;
    const editedWidget: Widget = customWidget;

    if (
      chartData.filters &&
      !chartData.filters.currentSelection &&
      !editedWidget?.hasMedusaQuery &&
      !isDiyWidgetEngine
    ) {
      defaultFilter = chartData.filters.dateFilter.find(
        (o: any) => o.displayName === 'Month To Date'
      );

      if (
        editedWidget.customWidgetConfig &&
        !(
          editedWidget.customWidgetConfig.filters &&
          editedWidget.customWidgetConfig.filters.currentSelection
        )
      ) {
        dateFilter = defaultFilter;
        const currentSelectionValue = {
          dateFilter: dateFilter
            ? {
                displayName: dateFilter.displayName,
                type: dateFilter.type,
                startDate: dateFilter.startDate ?? '',
                endDate: dateFilter.endDate ?? '',
                isEditable: dateFilter.isEditable,
              }
            : null,
        };
        // WHY: the config object is mutated in place (not via setState) so the very next render
        // of the preview already sees the default filter. Preserved from the original drawer.
        editedWidget.customWidgetConfig.filters = { currentSelection: currentSelectionValue };
      }

      setSelectedFilteringDateRange(
        dateFilter == undefined
          ? editedWidget.customWidgetConfig?.filters?.currentSelection?.dateFilter
          : defaultFilter
      );
    }
  };

  /** Stores the date ranges Guardrails offers and applies the default selection if needed. */
  const parseChartFilters = (chartData: any) => {
    if (chartData?.filters) {
      setChartFilters(chartData.filters.dateFilter);
      applyDefaultDateFilter(chartData);
    } else {
      setChartFilters([]);
    }
  };

  /** Commits a range picked in the calendar modal into both the config and local state. */
  const applyDateRange = (newValue: any) => {
    if (!newValue) return;

    const editedWidget: Widget = customWidget;
    if (editedWidget.customWidgetConfig) {
      const dateFilter: FilterDateOption = newValue.value;
      // WHY: only overwritten when a DateFilter already exists, mirroring the original guard that
      // prevents creating one for charts that never had it.
      if (
        editedWidget.customWidgetConfig.filters?.currentSelection &&
        editedWidget.customWidgetConfig.filters?.currentSelection?.dateFilter
      ) {
        editedWidget.customWidgetConfig.filters.currentSelection.dateFilter = dateFilter;
      }
    }
    setSelectedFilteringDateRange(newValue.value);
  };

  const handleHierarchyFilterChange = (newValue: any) => {
    const converted = convertJsonToHierarchyFilter(newValue?.SelectedFilters);
    if (newValue?.SelectedFilters !== undefined) setHasHierarchyFilters(true);
    setHierarchyFiltersValues([...converted]);
  };

  const handleAdvancedFilterChange = (newValue: any) => {
    const converted = convertJsonToAdvancedFilter(newValue?.SelectedFilters);
    if (newValue?.SelectedFilters !== undefined) setHasAdvancedFilters(true);
    setAdvancedFiltersValues([...converted]);
  };

  /**
   * Builds the `filters.currentSelection` payload persisted in the widget config.
   *
   * Each filter group falls back to the persisted value unless the user has a selection or has
   * explicitly touched that group.
   *
   * @param enterprise ENTERPRISE-ONLY: when `true` the date filter is stripped, because enterprise
   *                   widgets are not date-filtered and a stale MTD value would leak into them.
   */
  const buildCurrentSelection = (enterprise: boolean) => {
    const persisted = customWidget.customWidgetConfig?.filters?.currentSelection;

    const hierarchyFilters = hierarchyFiltersValues
      ? {
          hierarchyFilters:
            hierarchyFiltersValues.length > 0 || hasHierarchyFilters
              ? hierarchyFiltersValues
              : persisted?.hierarchyFilters,
        }
      : {};

    const advancedFilters = advancedFiltersValues
      ? {
          advancedFilters:
            advancedFiltersValues.length > 0 || hasAdvancedFilters
              ? advancedFiltersValues
              : persisted?.advancedFilters,
        }
      : {};

    const base = enterprise ? { ...persisted, dateFilter: undefined } : persisted;

    return { currentSelection: { ...base, ...hierarchyFilters, ...advancedFilters } };
  };

  return {
    chartFilters,
    selectedFilteringDateRange,
    hierarchyFiltersValues,
    advancedFiltersValues,
    parseChartFilters,
    applyDateRange,
    handleHierarchyFilterChange,
    handleAdvancedFilterChange,
    buildCurrentSelection,
  };
};
